import React from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { FileText, Download, Shield } from 'lucide-react';

const PolicyDocumentsPage = () => {
  const policyDocs = [
    {
      title: 'ARCA Constitution & Bylaws',
      description: 'Official governing documents outlining ARCA\'s mission, structure, and operational procedures.',
      category: 'Governance',
      lastUpdated: '2024',
      status: 'Available Soon'
    },
    {
      title: 'Code of Ethics for Respiratory Therapists',
      description: 'Professional standards and ethical guidelines for respiratory care practitioners across Africa.',
      category: 'Professional Standards',
      lastUpdated: '2023',
      status: 'Available Soon'
    },
    {
      title: 'Scope of Practice Guidelines',
      description: 'Comprehensive guidelines defining the professional scope of respiratory therapy practice.',
      category: 'Professional Standards',
      lastUpdated: '2024',
      status: 'Available Soon'
    },
    {
      title: 'Patient Safety & Quality Standards',
      description: 'Quality assurance protocols and patient safety standards for respiratory care services.',
      category: 'Clinical Standards',
      lastUpdated: '2024',
      status: 'Available Soon'
    },
    {
      title: 'Infection Control Protocols',
      description: 'Evidence-based infection prevention and control measures for respiratory therapy departments.',
      category: 'Clinical Standards',
      lastUpdated: '2023',
      status: 'Available Soon'
    },
    {
      title: 'Equipment Standards & Maintenance',
      description: 'Guidelines for respiratory equipment procurement, maintenance, and quality assurance.',
      category: 'Technical Standards',
      lastUpdated: '2024',
      status: 'Available Soon'
    }
  ];

  const categories = ['All', 'Governance', 'Professional Standards', 'Clinical Standards', 'Technical Standards'];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredDocs = selectedCategory === 'All' 
    ? policyDocs 
    : policyDocs.filter(doc => doc.category === selectedCategory);

  return (
    <PublicLayout>
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                   style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <Shield className="text-white" size={40} />
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">Policy Documents</h1>
              <p className="lead text-muted">
                Guidelines, protocols, and policy recommendations for respiratory care practice
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="row justify-content-center mb-4">
            <div className="col-lg-10">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`btn btn-sm ${
                      selectedCategory === category
                        ? 'text-white'
                        : 'btn-outline-secondary'
                    }`}
                    style={selectedCategory === category ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : {}}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Documents Grid */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row g-4 mb-5">
                {filteredDocs.map((doc, index) => (
                  <div key={index} className="col-md-6">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start mb-3">
                          <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0" 
                               style={{ width: '48px', height: '48px', backgroundColor: '#e8f5e9' }}>
                            <FileText style={{ color: '#6B8E23' }} size={24} />
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="fw-bold text-dark mb-2">{doc.title}</h5>
                            <p className="text-muted small mb-3">{doc.description}</p>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <span className="badge text-white" style={{ backgroundColor: '#6B8E23' }}>
                                {doc.category}
                              </span>
                              <span className="small text-muted">Updated: {doc.lastUpdated}</span>
                              <span className="badge bg-warning text-dark">{doc.status}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info Section */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-5">
                  <h3 className="h4 fw-bold text-dark mb-4">About Our Policy Documents</h3>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-shield-alt me-2" style={{ color: '#6B8E23' }}></i>
                        Standards & Guidelines
                      </h5>
                      <p className="text-muted">
                        Our policy documents are developed by expert committees and align with international 
                        best practices while considering the unique context of African healthcare systems.
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-users me-2" style={{ color: '#6B8E23' }}></i>
                        Stakeholder Input
                      </h5>
                      <p className="text-muted">
                        We welcome feedback from members, healthcare institutions, and regulatory bodies 
                        to ensure our policies are practical, relevant, and effective.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="card shadow border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                <div className="card-body p-5 text-center text-white">
                  <h3 className="h4 fw-bold mb-3">Policy Documents Coming Soon</h3>
                  <p className="mb-4">
                    We are currently finalizing our policy documentation framework. ARCA members 
                    will have priority access to all policy documents once published.
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-3">
                    <a href="/get-involved/become-member" className="btn btn-light btn-lg fw-bold">Become a Member</a>
                    <a href="/contact" className="btn btn-outline-light btn-lg">Request Information</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PolicyDocumentsPage;
